import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { kebabCase } from 'es-toolkit';
import { PATHS } from '@belong/common';
import { fetchHome } from 'store/redux/homes/actions';
import {
  CommonFlowStepModel,
  FlowFlowModel,
  FlowModel,
  ResidentAccountSummaryItem,
} from 'api/models';
import Home from 'models/home/Home';
import { EmployeeAssignmentTypes, FlowStatus } from 'models/enums';
import { EMPLOYEE_TITLES } from 'strings/common.strings';
import { selectIsUserLoggedIn, selectUser } from 'store/redux/user/selectors';
import { fetchPaymentMethods, fetchUser } from 'store/redux/user/actions';
import {
  findStepFromUrl,
  getNextAfterLastCompletedOrCurrent,
} from './leaseSigningUtils';
import { useAppDispatch } from 'store/redux/use-app-dispatch';

// Constants
const FLOW_TYPE_LEASE_SIGNING = 'LeaseSigning';
const EXCLUDED_STEP_NAMES: string[] = [];

type _RouteParams = {
  homeId?: string;
};

/**
 * A visible step of the flow, as `steps` below builds it: the step from
 * `FlowModel.steps` plus the ordering and routing fields this hook derives.
 */
type LeaseSigningStep = CommonFlowStepModel & {
  index: number;
  name: CommonFlowStepModel['stepName'];
  path: string;
};

/**
 * `currentStep` is that step merged with the step's own payload from
 * `/residents/lease-signing/{flowId}/{stepSlug}/{stepId}`. Every step returns a
 * different shape and the response arrives through `dispatch` untyped, so the
 * payload half is genuinely unknown here — `isRenewal` and `isCosigner`, which
 * this hook returns, come from it rather than from the step itself.
 */
type LeaseSigningCurrentStep = LeaseSigningStep & Record<string, any>;

export function useLeaseSigningFlow() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { path } = useRouteMatch();

  // Get user info for validation
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const user = useSelector(selectUser);

  // State
  const [leaseSigningData, setLeaseSigningData] = useState<FlowModel | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] =
    useState<LeaseSigningCurrentStep | null>(null);
  const [home, setHome] = useState<Home>();
  const [lease, setLease] = useState<ResidentAccountSummaryItem>();
  const [flowId, setFlowId] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const userFullName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(' ');

  // Callbacks
  const exitSigningFlow = useCallback(
    (reason: string, details?: any, customErrorMessage?: string) => {
      const errorContext = {
        reason,
        timestamp: new Date().toISOString(),
        pathname: location.pathname,
        ...details,
      };

      console.error('[Lease Signing Flow] Exit triggered:', errorContext);

      if (customErrorMessage) {
        history.push(
          `${PATHS.RESIDENTS_ACCOUNT_TOURS_AND_APPS}?error=${encodeURIComponent(
            customErrorMessage,
          )}`,
        );
        return;
      }

      history.push(
        `${PATHS.RESIDENTS_ACCOUNT_TOURS_AND_APPS}?error=${encodeURIComponent(
          `There was an error with your lease. Please try again or contact support.`,
        )}`,
      );
    },
    [history, location.pathname],
  );

  // Ensure user profile and payment methods are loaded for payment selection
  useEffect(() => {
    if (!isUserLoggedIn) return;

    (async () => {
      try {
        await Promise.all([
          dispatch(fetchUser()),
          dispatch(fetchPaymentMethods()),
        ]);
      } catch (err) {
        console.error('Error fetching user data for lease signing flow:', err);
      }
    })();
  }, [dispatch, isUserLoggedIn]);

  // Fetch initial lease signing data
  useEffect(() => {
    const fetchResidentFlows = async () => {
      const response = await dispatch({
        promise: ({ client }: { client: any }) =>
          client.get(`/residents/flows`),
        auth: true,
      });
      return response?.find(
        (flow: FlowFlowModel) =>
          flow.flowType === FLOW_TYPE_LEASE_SIGNING &&
          flow.status !== FlowStatus.Completed &&
          flow.status !== FlowStatus.Cancelled,
      );
    };

    const fetchLeaseSigningDetails = async (flowId: string) => {
      return await dispatch({
        promise: ({ client }: { client: any }) =>
          client.get(`/residents/lease-signing/${flowId}`),
        auth: true,
      });
    };

    const fetchHomeAndLeaseData = async (
      homeUniqueId: string,
      leaseUniqueId: string,
    ) => {
      return await Promise.all([
        dispatch(fetchHome(homeUniqueId, false)),
        dispatch({
          promise: ({ client }: { client: any }) =>
            client.get(
              `/residents/units/${homeUniqueId}?leaseId=${leaseUniqueId}`,
            ),
          auth: true,
        }),
      ]);
    };

    const fetchLeaseSigningData = async () => {
      try {
        setIsLoading(true);
        setError(false);

        // Step 1: Get the lease signing flow
        const currentFlow = await fetchResidentFlows();
        if (!currentFlow) {
          exitSigningFlow(
            'No active lease signing flow found',
            {
              reason_detail: 'fetchResidentFlows returned no flow',
            },
            `This user has no pending leases to be signed. You're signed in as ${
              userFullName || 'this user'
            } (${user?.email || 'unknown email'}). If this is not the intended account, sign in to the correct account to sign the lease.`,
          );
          return;
        }

        const currentFlowId = currentFlow.uniqueId;
        setFlowId(currentFlowId);

        // Step 2: Fetch lease signing details
        const leaseSigningResponse =
          await fetchLeaseSigningDetails(currentFlowId);
        const homeUniqueId = leaseSigningResponse?.configuration?.homeUniqueId;
        const leaseId = leaseSigningResponse?.configuration.leaseId;
        const leaseUniqueId = leaseSigningResponse?.configuration.leaseUniqueId;

        if (!homeUniqueId || !leaseId) {
          exitSigningFlow('Missing home or lease ID in configuration', {
            homeUniqueId,
            leaseId,
            leaseUniqueId,
            flowId: currentFlowId,
          });
          return;
        }

        setLeaseSigningData(leaseSigningResponse);

        // Step 3: Fetch home and lease data in parallel
        const [homeResponse, unitDetail] = await fetchHomeAndLeaseData(
          homeUniqueId,
          leaseUniqueId,
        );

        if (!homeResponse || !unitDetail) {
          exitSigningFlow('Failed to fetch home or unit details', {
            hasHomeResponse: !!homeResponse,
            hasUnitDetail: !!unitDetail,
            homeUniqueId,
            leaseUniqueId,
            flowId: currentFlowId,
          });
          return;
        }

        setHome(new Home(homeResponse));

        // Convert ResidentUnitDetail to ResidentAccountSummaryItem format
        const leaseData: ResidentAccountSummaryItem = {
          leaseInfo: unitDetail.leaseDetail,
          unitInfo: unitDetail.basicInfo,
          address: undefined, // unitDetail doesn't have address directly
        };

        setLease(leaseData);
        setIsInitialDataLoaded(true);
      } catch (err) {
        console.error('Error fetching lease signing data:', err);
        setError(true);
        exitSigningFlow('Exception during lease signing data fetch', {
          error: err instanceof Error ? err.message : String(err),
          errorStack: err instanceof Error ? err.stack : undefined,
          errorType: err?.constructor?.name,
        });
        setIsLoading(false);
        setIsInitialDataLoaded(true);
      }
      // Note: isLoading is set to false in the second useEffect after step data is fetched
    };

    fetchLeaseSigningData();
  }, [dispatch, exitSigningFlow, isUserLoggedIn, history, userFullName]);

  // Compute visible steps from lease signing data
  const steps = useMemo(() => {
    if (!leaseSigningData?.steps) return [];

    const homeUniqueId = leaseSigningData.configuration?.homeUniqueId;
    if (!homeUniqueId) return [];

    return leaseSigningData.steps
      .filter(
        (step) =>
          !step.isHidden &&
          !(
            step.stepName !== undefined &&
            EXCLUDED_STEP_NAMES.includes(step.stepName)
          ),
      )
      .map((step, index) => ({
        ...step,
        index,
        name: step.stepName,
        path: `${PATHS.LEASE_SIGNING_FLOW_V2}/${step.stepName}`,
      }));
  }, [leaseSigningData, path]);

  // Compute employee assignment based on lease type
  const { employeeAssignment: _employeeAssignment, employee } = useMemo(() => {
    if (!home) {
      return {
        employeeAssignment: {},
        employee: { employeeAssignment: {}, employee: {} },
      };
    }

    const isRenew = (home.leaseOffered?.renewalNumber ?? 0) > 0;
    const assignmentType = isRenew
      ? EmployeeAssignmentTypes.Concierge
      : EmployeeAssignmentTypes.ResidentGrowth;
    const assignment = isRenew
      ? home.employees?.concierge
      : home.employees?.residentGrowth || {};

    const assignmentWithTitle = {
      ...assignment,
      // `models/enums` is plain JS, so its members widen to `string` and cannot
      // index `EMPLOYEE_TITLES`. Both branches above are keys of it.
      jobTitle: EMPLOYEE_TITLES[assignmentType as keyof typeof EMPLOYEE_TITLES],
    };

    return {
      employeeAssignment: assignmentWithTitle,
      employee: {
        employeeAssignment: { assignmentType },
        employee: {
          ...assignmentWithTitle,
          email: assignmentWithTitle?.workEmail,
        },
      },
    };
  }, [home]);

  // Fetch and validate step data based on URL
  useEffect(() => {
    const fetchStepData = async () => {
      try {
        // Early return if prerequisites not met
        if (!isInitialDataLoaded || !steps.length || !flowId) {
          return;
        }

        const stepFromUrl = findStepFromUrl(location.pathname, steps);
        if (!stepFromUrl) {
          setIsLoading(false);
          return;
        }

        const correspondingStep = getNextAfterLastCompletedOrCurrent(
          steps,
          stepFromUrl.id,
        );
        if (!correspondingStep) {
          setIsLoading(false);
          return;
        }

        // Redirect if user is trying to access wrong step
        if (correspondingStep.stepName !== stepFromUrl.stepName) {
          history.push(correspondingStep.path);
          setIsLoading(false);
          return;
        }

        // Fetch step details
        setError(false);

        // INSERT_YOUR_CODE
        await new Promise((resolve) => setTimeout(resolve, 1500));

        const stepSlug = kebabCase(correspondingStep.stepName);
        const stepResponse = await dispatch({
          promise: ({ client }: { client: any }) =>
            client.get(
              `/residents/lease-signing/${flowId}/${stepSlug}/${correspondingStep.id}`,
            ),
          auth: true,
        });

        setCurrentStep({ ...correspondingStep, ...stepResponse });
      } catch (err) {
        console.error('Error fetching step data:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStepData();
  }, [
    steps,
    location.pathname,
    flowId,
    dispatch,
    history,
    isInitialDataLoaded,
  ]);

  const isRenewal = currentStep?.isRenewal;

  const leaseUniqueId = leaseSigningData?.configuration?.leaseUniqueId;

  return {
    // Data
    leaseSigningData,
    home,
    leaseUniqueId,
    currentStep,
    steps,
    employee,
    isRenewal,
    isLoading,
    error,
    isCosigner: currentStep?.isCosigner ?? false,
    lease,
  };
}
